import {
  PERCEPCOES,
  ROTULO_PERCEPCAO,
  ROTULO_STATUS_LEAD,
  TEXTO_ATENDIMENTO_MINIMO,
  schemaRegistrarInteracao,
  type EntradaRegistrarInteracao,
  type LeadDetalhado,
  type StatusLead,
} from '@baiocchi/shared'
import {
  Alerta,
  AreaTexto,
  Botao,
  CabecalhoCartao,
  Caixa,
  Campo,
  Cartao,
  ContadorTexto,
  CorpoCartao,
  Dica,
  Entrada,
  Selecao,
  useAvisos,
} from '@baiocchi/ui'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { chaves } from '../../api/chaves'
import { leadsApi } from '../../api/leads'
import { IconeArquivo } from '../../componentes/icones'
import { AJUDA_SITUACAO } from '../../dominio/rotulos'
import {
  dicaSeIndisponivel,
  opcoesDeCatalogo,
  useCatalogoAtivo,
} from '../../ganchos/usar-catalogos'
import { useFormulario } from '../../ganchos/usar-formulario'
import { localParaInstante } from '../../utilitarios/formatar'

interface EstadoInteracao {
  statusNovo: string
  mensagem: string
  percepcao: string
  usarProduto: boolean
  produtoAssociadoId: string
  motivoVendaNaoRealizadaId: string
  vendaRealizadaEm: string
  valorNegocio: string
  agendamentoIniciaEm: string
  agendamentoMotivoId: string
  agendamentoObservacao: string
  registrarTentativaContato: boolean
}

/**
 * Formulário "Registrar Interação" — o coração do atendimento (TELA-05).
 *
 * É o **único** caminho para mudar o estado de um lead: não existe
 * `PATCH /leads/{uuid}/status`, porque status sem interação é proibido (RN-22).
 *
 * A lista de situações vem de `lead.transicoesPermitidas`, que o servidor calcula
 * a partir da máquina de estados (RN-20). A tela **não** replica a tabela de
 * transições: haveria duas definições, e a do cliente seria a que o atendente
 * enxerga — ele descobriria a regra real só no `422`.
 *
 * Campos condicionais seguem RN-23 e aparecem conforme a situação escolhida. A
 * validação do cliente é conveniência; o servidor valida de novo e devolve **todos**
 * os campos faltantes de uma vez (RNF-115).
 */
export function FormularioRegistrarInteracao({ lead }: { lead: LeadDetalhado }) {
  const clienteConsulta = useQueryClient()
  const avisos = useAvisos()
  const [arquivosSelecionados, setArquivosSelecionados] = useState<File[]>([])

  const produtos = useCatalogoAtivo('produtos-associados')
  const motivosPerda = useCatalogoAtivo('motivos-venda-nao-realizada')
  const motivosAgendamento = useCatalogoAtivo('motivos-agendamento')

  const formulario = useFormulario<EstadoInteracao>({
    // Pré-selecionada com a situação atual: reenviar o status corrente é
    // anotação, não erro (decisão D3), e é o caso mais comum — o atendente quer
    // registrar a conversa sem mudar o estado.
    statusNovo: lead.status,
    mensagem: '',
    percepcao: lead.percepcao,
    usarProduto: false,
    produtoAssociadoId: lead.produtoAssociado ? String(lead.produtoAssociado.id) : '',
    motivoVendaNaoRealizadaId: '',
    vendaRealizadaEm: '',
    valorNegocio: '',
    agendamentoIniciaEm: '',
    agendamentoMotivoId: '',
    agendamentoObservacao: '',
    registrarTentativaContato: false,
  })

  const { valores, definir } = formulario
  const status = valores.statusNovo as StatusLead

  const exigeAgendamento = status === 'AGENDADO'
  const exigeMotivoPerda = status === 'VENDA_NAO_REALIZADA'
  const exigeVenda = status === 'VENDA_REALIZADA'
  const ehAnotacao = status === lead.status && status !== 'AGENDADO'

  /** Situação atual + destinos válidos, sem duplicar. */
  const opcoesSituacao = [
    { valor: lead.status, rotulo: `${ROTULO_STATUS_LEAD[lead.status]} (manter — só anotar)` },
    ...lead.transicoesPermitidas
      .filter((destino) => destino !== lead.status)
      .map((destino) => ({ valor: destino, rotulo: ROTULO_STATUS_LEAD[destino] })),
  ]

  function montarCorpo(estado: EstadoInteracao): unknown {
    return {
      statusNovo: estado.statusNovo,
      mensagem: estado.mensagem,
      percepcao: estado.percepcao,
      produtoAssociadoId:
        estado.usarProduto && estado.produtoAssociadoId ? Number(estado.produtoAssociadoId) : null,
      motivoVendaNaoRealizadaId: estado.motivoVendaNaoRealizadaId
        ? Number(estado.motivoVendaNaoRealizadaId)
        : null,
      vendaRealizadaEm: estado.vendaRealizadaEm
        ? localParaInstante(estado.vendaRealizadaEm)
        : null,
      valorNegocio: estado.valorNegocio.trim() || null,
      agendamento:
        estado.agendamentoIniciaEm && estado.agendamentoMotivoId
          ? {
              motivoId: Number(estado.agendamentoMotivoId),
              iniciaEm: localParaInstante(estado.agendamentoIniciaEm),
              observacao: estado.agendamentoObservacao.trim() || null,
            }
          : null,
      registrarTentativaContato: estado.registrarTentativaContato,
    }
  }

  async function submeter(evento: React.FormEvent) {
    evento.preventDefault()

    const concluiu = await formulario.enviar<EntradaRegistrarInteracao>(
      schemaRegistrarInteracao,
      montarCorpo,
      async (corpo) => {
        const resultado = await leadsApi.registrarInteracao(lead.uuid, corpo)

        // O servidor mexeu em lead, timeline, painel e agregados na mesma
        // transação: invalidar só o detalhe deixaria a lista e o painel mentindo.
        void clienteConsulta.invalidateQueries({ queryKey: chaves.leads.todos })
        void clienteConsulta.invalidateQueries({ queryKey: chaves.painel.todos })
        void clienteConsulta.invalidateQueries({ queryKey: chaves.agendamentos.todos })
        void clienteConsulta.invalidateQueries({ queryKey: chaves.indicadores.todos })

        avisos.sucesso(
          'Interação registrada',
          resultado.agendamentoCriado ? 'Agendamento criado junto com a interação.' : undefined,
        )
      },
    )

    if (concluiu) {
      formulario.reiniciar({
        ...valores,
        mensagem: '',
        agendamentoIniciaEm: '',
        agendamentoMotivoId: '',
        agendamentoObservacao: '',
        motivoVendaNaoRealizadaId: '',
        vendaRealizadaEm: '',
        valorNegocio: '',
        registrarTentativaContato: false,
      })
      setArquivosSelecionados([])
    }
  }

  return (
    <Cartao semPadding>
      <CabecalhoCartao
        titulo="Registrar interação"
        descricao="Toda mudança de situação passa por aqui, e nada pode ser editado depois."
      />
      <form onSubmit={submeter} noValidate>
        <CorpoCartao className="flex flex-col gap-4">
          {formulario.erroGeral ? (
            <Alerta tom="perigo" titulo="Não foi possível registrar">
              <p>{formulario.erroGeral}</p>
              {formulario.erroApi?.detalhes.length ? (
                <ul className="mt-2 list-inside list-disc">
                  {formulario.erroApi.detalhes.map((detalhe, indice) => (
                    <li key={`${detalhe.campo}-${indice}`}>{detalhe.mensagem}</li>
                  ))}
                </ul>
              ) : null}
            </Alerta>
          ) : null}

          <Campo
            rotulo={
              <span className="inline-flex items-center gap-1.5">
                Situação do lead
                <Dica texto={AJUDA_SITUACAO} />
              </span>
            }
            obrigatorio
            erro={formulario.erros.statusNovo}
            dica={
              ehAnotacao
                ? 'Mantendo a situação, a interação entra como anotação, sem transição.'
                : undefined
            }
          >
            {(atributos) => (
              <Selecao
                {...atributos}
                value={valores.statusNovo}
                temErro={atributos.temErro}
                opcoes={opcoesSituacao}
                onChange={(evento) => definir('statusNovo', evento.target.value)}
              />
            )}
          </Campo>

          <Campo
            rotulo="Mensagem / observações"
            obrigatorio
            erro={formulario.erros.mensagem}
            acessorio={<ContadorTexto valor={valores.mensagem} minimo={TEXTO_ATENDIMENTO_MINIMO} />}
          >
            {(atributos) => (
              <AreaTexto
                {...atributos}
                rows={5}
                value={valores.mensagem}
                temErro={atributos.temErro}
                placeholder="O que foi conversado, o que o cliente pediu e qual é o próximo passo."
                onChange={(evento) => definir('mensagem', evento.target.value)}
              />
            )}
          </Campo>

          <Campo rotulo="Percepção" obrigatorio erro={formulario.erros.percepcao}>
            {(atributos) => (
              <Selecao
                {...atributos}
                value={valores.percepcao}
                temErro={atributos.temErro}
                opcoes={PERCEPCOES.map((percepcao) => ({
                  valor: percepcao,
                  rotulo: ROTULO_PERCEPCAO[percepcao],
                }))}
                onChange={(evento) => definir('percepcao', evento.target.value)}
              />
            )}
          </Campo>

          {/* --- Campos condicionais (RN-23) --- */}
          {exigeAgendamento && (
            <fieldset className="grid gap-3 rounded-md border border-borda bg-superficie-2 p-3 sm:grid-cols-2">
              <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-conteudo-suave">
                Agendamento obrigatório
              </legend>
              <Campo
                rotulo="Data e hora"
                obrigatorio
                erro={formulario.erros['agendamento.iniciaEm']}
                dica="Precisa ser no futuro."
              >
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    type="datetime-local"
                    value={valores.agendamentoIniciaEm}
                    temErro={atributos.temErro}
                    onChange={(evento) => definir('agendamentoIniciaEm', evento.target.value)}
                  />
                )}
              </Campo>
              <Campo
                rotulo="Motivo"
                obrigatorio
                erro={formulario.erros['agendamento.motivoId']}
                dica={dicaSeIndisponivel(motivosAgendamento, 'GET /api/v1/motivos-agendamento')}
              >
                {(atributos) => (
                  <Selecao
                    {...atributos}
                    vazio="Selecione o motivo"
                    value={valores.agendamentoMotivoId}
                    temErro={atributos.temErro}
                    opcoes={opcoesDeCatalogo(motivosAgendamento.data?.dados)}
                    onChange={(evento) => definir('agendamentoMotivoId', evento.target.value)}
                  />
                )}
              </Campo>
              <Campo rotulo="Observação do compromisso" className="sm:col-span-2">
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    value={valores.agendamentoObservacao}
                    onChange={(evento) => definir('agendamentoObservacao', evento.target.value)}
                  />
                )}
              </Campo>
            </fieldset>
          )}

          {exigeMotivoPerda && (
            <Campo
              rotulo="Motivo da venda não realizada"
              obrigatorio
              erro={formulario.erros.motivoVendaNaoRealizadaId}
              dica={dicaSeIndisponivel(
                motivosPerda,
                'GET /api/v1/motivos-venda-nao-realizada',
                'Somente motivos ativos. É o campo que alimenta o relatório de perdas.',
              )}
            >
              {(atributos) => (
                <Selecao
                  {...atributos}
                  vazio="Selecione o motivo"
                  value={valores.motivoVendaNaoRealizadaId}
                  temErro={atributos.temErro}
                  opcoes={opcoesDeCatalogo(motivosPerda.data?.dados)}
                  onChange={(evento) => definir('motivoVendaNaoRealizadaId', evento.target.value)}
                />
              )}
            </Campo>
          )}

          {exigeVenda && (
            <fieldset className="grid gap-3 rounded-md border border-sucesso/30 bg-sucesso-suave p-3 sm:grid-cols-2">
              <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-sucesso">
                Dados do fechamento
              </legend>
              <Campo
                rotulo="Data do fechamento"
                obrigatorio
                erro={formulario.erros.vendaRealizadaEm}
              >
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    type="datetime-local"
                    value={valores.vendaRealizadaEm}
                    temErro={atributos.temErro}
                    onChange={(evento) => definir('vendaRealizadaEm', evento.target.value)}
                  />
                )}
              </Campo>
              <Campo
                rotulo="Valor do negócio"
                erro={formulario.erros.valorNegocio}
                dica="Formato 350000.00. Opcional até o negócio definir os dados do fechamento."
              >
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    inputMode="decimal"
                    placeholder="350000.00"
                    value={valores.valorNegocio}
                    temErro={atributos.temErro}
                    onChange={(evento) => definir('valorNegocio', evento.target.value)}
                  />
                )}
              </Campo>
            </fieldset>
          )}

          <Caixa
            rotulo="Vincular produto associado"
            checked={valores.usarProduto}
            onChange={(evento) => definir('usarProduto', evento.target.checked)}
          />

          {valores.usarProduto && (
            <Campo rotulo="Produto associado" erro={formulario.erros.produtoAssociadoId}>
              {(atributos) => (
                <Selecao
                  {...atributos}
                  vazio="Selecione o produto"
                  value={valores.produtoAssociadoId}
                  opcoes={opcoesDeCatalogo(produtos.data?.dados)}
                  onChange={(evento) => definir('produtoAssociadoId', evento.target.value)}
                />
              )}
            </Campo>
          )}

          <Caixa
            rotulo="Registrar como tentativa de contato"
            dica="Alimenta o selo Abordagem N e a temperatura do lead."
            checked={valores.registrarTentativaContato}
            onChange={(evento) => definir('registrarTentativaContato', evento.target.checked)}
          />

          {/* Anexos — a API ainda não tem POST /arquivos (ver §10). */}
          <Campo
            rotulo="Arquivos"
            dica="O envio de anexos depende de POST /arquivos, que ainda não foi publicado pela API. A timeline já exibe anexos que venham de outra origem."
          >
            {(atributos) => (
              <div className="flex flex-col gap-2">
                <input
                  {...atributos}
                  type="file"
                  multiple
                  disabled
                  onChange={(evento) =>
                    setArquivosSelecionados(Array.from(evento.target.files ?? []))
                  }
                  className="w-full cursor-not-allowed rounded-md border border-dashed border-borda-forte bg-superficie-2 px-3 py-2 text-sm text-conteudo-fraco file:mr-3 file:rounded file:border-0 file:bg-superficie-3 file:px-3 file:py-1 file:text-xs"
                />
                {arquivosSelecionados.length > 0 && (
                  <ul className="flex flex-col gap-1 text-xs text-conteudo-suave">
                    {arquivosSelecionados.map((arquivo) => (
                      <li key={arquivo.name} className="flex items-center gap-1.5">
                        <IconeArquivo className="h-3.5 w-3.5" />
                        {arquivo.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </Campo>

          <Botao
            type="submit"
            larguraTotal
            tamanho="lg"
            variante="sucesso"
            carregando={formulario.enviando}
            disabled={valores.mensagem.trim().length < TEXTO_ATENDIMENTO_MINIMO}
          >
            Registrar interação
          </Botao>
        </CorpoCartao>
      </form>
    </Cartao>
  )
}
